import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const Profile = () => {
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state.auth.userInfo);
  const firstName = userInfo?.userInfo?.first_name || "";
  const lastName = userInfo?.userInfo?.last_name || "";
  const email = userInfo?.userInfo?.email || "";
  const device = userInfo?.userInfo?.seat_capability || "";

  let designation = "";
  const rawRole =
    userInfo?.userInfo?.designation ||
    userInfo?.userInfo?.role ||
    userInfo?.userInfo?.role_name;
  if (rawRole) {
    if (typeof rawRole === "object" && rawRole.name) {
      designation = rawRole.name;
    } else if (typeof rawRole === "string") {
      designation = rawRole;
    }
  }

  const InfoCard = ({ title, value }) => (
    <div className="flex flex-col rounded-xl border border-gray-200 bg-gray-50/80 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
      <p className="text-[11px] font-black uppercase tracking-[0.16em] text-gray-500">
        {title}
      </p>
      <p className="mt-1 text-sm font-semibold text-black truncate">
        {value || "—"}
      </p>
    </div>
  );

  return (
    <div className="container mx-auto pb-10">
      <div className="mb-4">
        <h1 className="text-2xl font-bold tracking-tight text-black sm:text-3xl">Settings</h1>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        
        {/* Sidebar */}
        <div className="w-full lg:w-64 shrink-0">
          <div className="overflow-hidden rounded-[28px] border border-gray-200 bg-white shadow-[0_18px_48px_rgba(0,0,0,0.06)]">
            <div className="p-2 flex flex-row lg:flex-col gap-1 overflow-x-auto hide-scrollbar">
              <button
                type="button"
                className="flex items-center gap-3 w-full rounded-2xl bg-black px-4 py-3 text-left transition-all shadow-[0_14px_32px_rgba(0,0,0,0.18)]"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/20 text-white">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-bold text-white">Profile</p>
                  <p className="text-[11px] font-medium text-gray-300">Personal info</p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => navigate("/change-password")}
                className="flex items-center gap-3 w-full rounded-2xl bg-transparent px-4 py-3 text-left transition-all hover:bg-gray-100"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-black">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <rect x="5" y="11" width="14" height="10" rx="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M8 11V8a4 4 0 118 0v3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-bold text-black">Security</p>
                  <p className="text-[11px] font-medium text-gray-500">Change password</p>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden rounded-[28px] border border-gray-200 bg-white shadow-[0_18px_48px_rgba(0,0,0,0.06)]">
          <div className="border-b border-gray-100 bg-[linear-gradient(180deg,#ffffff_0%,#fbfbfb_100%)] px-5 py-5 sm:px-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center justify-between">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.22em] text-gray-400">
                  Account
                </p>
                <h2 className="mt-1 text-2xl font-semibold tracking-[-0.03em] text-black">
                  My Profile
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Manage your personal information
                </p>
              </div>
            </div>
          </div>

          <div className="px-5 py-6 sm:px-6">
            <div className="flex flex-col sm:flex-row items-center gap-6 mb-8">
              <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-black text-3xl font-black text-white shadow-[0_14px_32px_rgba(0,0,0,0.18)]">
                {(firstName?.charAt(0) || "")}{(lastName?.charAt(0) || "")}
              </div>
              <div className="text-center sm:text-left">
                <h3 className="text-xl font-bold tracking-tight text-black">
                  {firstName} {lastName}
                </h3>
                <p className="text-sm font-medium text-gray-500 mb-2">
                  {email}
                </p>
                <span className="inline-flex items-center rounded-full bg-black px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">
                  {designation || "No Designation"}
                </span>
              </div>
            </div>

            <h4 className="text-[11px] font-black uppercase tracking-[0.22em] text-gray-400 mb-4">
              Personal Details
            </h4>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <InfoCard title="First Name" value={firstName} />
              <InfoCard title="Last Name" value={lastName} />
              <InfoCard title="Email Address" value={email} />
              <InfoCard title="Designation" value={designation} />
              <InfoCard title="Assigned Device" value={device} />
              <InfoCard title="Status" value="Active" />
            </div>
          </div>
        </div>
        
      </div>
      
      {/* CSS for hiding scrollbar on horizontal nav */}
      <style dangerouslySetInnerHTML={{__html: `
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}} />
    </div>
  );
};

export default Profile;
